import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SubHeader from '../../layout/components/SubHeader';
import DashboardBarChart from '../dashboard/subparts/charts/DashboardBarChart';
import DashboardPieChart from '../dashboard/subparts/charts/DashboardPieChart';
import { VIBRANT_PALETTE } from '../dashboard/dashboardChartColors';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import surveyService from '../../../services/survey.service';
import './Surveys.css';
import './SurveyResultPage.css';

const STATUS_COLORS = {
    Completed: '#22c55e',
    Partial: '#f59e0b',
    Abandoned: '#ef4444',
};

const CHANNEL_COLORS = {
    Link: '#6366f1',
    Email: '#06b6d4',
    SMS: '#ec4899',
};

const RATING_COLORS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'];

const formatCount = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0';
    return number.toLocaleString();
};

const colorizeSeries = (series = [], offset = 0, colorMap = null) =>
    series.map((item, index) => ({
        ...item,
        color:
            item.color ||
            colorMap?.[item.label] ||
            VIBRANT_PALETTE[(index + offset) % VIBRANT_PALETTE.length],
    }));

const bucketsToSeries = (buckets = [], offset = 0, isRating = false) =>
    buckets.map((bucket, index) => {
        const label = bucket.label || bucket.value || '—';
        let color = VIBRANT_PALETTE[(index + offset) % VIBRANT_PALETTE.length];
        if (isRating) {
            const rating = Number(bucket.value ?? bucket.label);
            if (Number.isFinite(rating) && rating >= 1 && rating <= RATING_COLORS.length) {
                color = RATING_COLORS[rating - 1];
            } else if (buckets.length <= RATING_COLORS.length) {
                color = RATING_COLORS[index % RATING_COLORS.length];
            }
        }
        return {
            label,
            value: Number(bucket.count) || 0,
            color,
        };
    });

function SurveyGraphsPage() {
    const { surveyId } = useParams();
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [survey, setSurvey] = useState(null);
    const [stats, setStats] = useState(null);
    const [aggregates, setAggregates] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadGraphs = useCallback(async () => {
        if (!clinicId || !surveyId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const [surveyRes, statsRes, aggregatesRes] = await Promise.all([
                surveyService.getClinicSurveyById(clinicId, surveyId),
                surveyService.getClinicSurveyResultStats(clinicId, surveyId).catch(() => null),
                surveyService.getClinicSurveyQuestionAggregates(clinicId, surveyId).catch(() => null),
            ]);
            setSurvey(surveyRes.data?.survey || null);
            setStats(statsRes?.data || null);
            setAggregates(aggregatesRes?.data?.questions || []);
        } catch (error) {
            console.error('Failed to load survey graphs', error);
            toast.error(error?.response?.data?.message || 'Could not load survey graphs.');
            setSurvey(null);
            setStats(null);
            setAggregates([]);
        } finally {
            setLoading(false);
        }
    }, [clinicId, surveyId]);

    useEffect(() => {
        loadGraphs();
    }, [loadGraphs]);

    const statusSeries = useMemo(
        () =>
            colorizeSeries(
                [
                    { label: 'Completed', value: stats?.completed || 0 },
                    { label: 'Partial', value: stats?.partial || 0 },
                    { label: 'Abandoned', value: stats?.abandoned || 0 },
                ].filter((item) => item.value > 0),
                0,
                STATUS_COLORS
            ),
        [stats]
    );

    const channelSeries = useMemo(
        () =>
            colorizeSeries(
                [
                    { label: 'Link', value: stats?.linkChannel || 0 },
                    { label: 'Email', value: stats?.emailChannel || 0 },
                    { label: 'SMS', value: stats?.smsChannel || 0 },
                ].filter((item) => item.value > 0),
                0,
                CHANNEL_COLORS
            ),
        [stats]
    );

    const chartableQuestions = useMemo(
        () =>
            (aggregates || []).filter(
                (question) => question.chartable && Array.isArray(question.buckets) && question.buckets.length > 0
            ),
        [aggregates]
    );

    const hasOverviewCharts = statusSeries.length > 0 || channelSeries.length > 0;
    const hasQuestionCharts = chartableQuestions.length > 0;

    return (
        <div className="ecx-page surveys-page survey-results-page ecx-page--full-width">
            <SubHeader
                title={survey?.title ? `${survey.title} — Graphs` : 'Survey graphs'}
                description="Visual breakdown of survey submissions and question answers"
                icon="bi-bar-chart-line"
                containerClass="w-100 px-4"
            >
                <div className="d-flex gap-2">
                    <Link
                        to={`/surveys/${surveyId}/results`}
                        className="btn btn-sm btn-outline-secondary rounded-pill"
                    >
                        <i className="bi bi-table me-1" />
                        Results
                    </Link>
                    <Link
                        to={`/surveys/${surveyId}/edit`}
                        className="btn btn-sm btn-outline-secondary rounded-pill"
                    >
                        <i className="bi bi-pencil me-1" />
                        Edit survey
                    </Link>
                    <Link to="/surveys" className="btn btn-sm btn-outline-secondary rounded-pill">
                        <i className="bi bi-chevron-left me-1" />
                        Back
                    </Link>
                </div>
            </SubHeader>

            <div className="ecx-page-body">
                {loading ? (
                    <div className="ecx-page-card ecx-page-panel">
                        <div className="ecx-page-panel__body py-5 text-center text-muted">
                            Loading survey graphs…
                        </div>
                    </div>
                ) : !survey ? (
                    <div className="ecx-page-card ecx-page-panel">
                        <div className="ecx-page-panel__body py-5 text-center">
                            <h2 className="h5 mb-2">Survey not found</h2>
                            <p className="text-muted mb-0">
                                This survey may have been deleted or is unavailable.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="survey-results__charts">
                        {hasOverviewCharts ? (
                            <div className="survey-results__chart-grid survey-results__chart-grid--overview">
                                {statusSeries.length > 0 ? (
                                    <div className="ecx-page-card survey-results__chart-card">
                                        <div className="survey-results__chart-header">
                                            <h3 className="h6 mb-0">Response status</h3>
                                            <span className="text-muted small">
                                                {formatCount(stats?.total)} total
                                            </span>
                                        </div>
                                        <DashboardPieChart
                                            series={statusSeries}
                                            solidColors
                                            showDataLabels
                                        />
                                    </div>
                                ) : null}
                                {channelSeries.length > 0 ? (
                                    <div className="ecx-page-card survey-results__chart-card">
                                        <div className="survey-results__chart-header">
                                            <h3 className="h6 mb-0">Response channel</h3>
                                        </div>
                                        <DashboardBarChart series={channelSeries} solidColors />
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        {hasQuestionCharts ? (
                            <div className="survey-results__chart-grid">
                                {chartableQuestions.map((question, questionIndex) => {
                                    const isRating =
                                        String(question.type || '').toLowerCase() === 'rating';
                                    const series = bucketsToSeries(
                                        question.buckets,
                                        questionIndex * 3,
                                        isRating
                                    );
                                    const usePie =
                                        ['single_choice', 'dropdown'].includes(
                                            String(question.type || '').toLowerCase()
                                        ) && series.length <= 6;
                                    return (
                                        <div
                                            key={question.questionId}
                                            className="ecx-page-card survey-results__chart-card"
                                        >
                                            <div className="survey-results__chart-header">
                                                <div>
                                                    <h3 className="h6 mb-1">
                                                        {question.title || 'Question'}
                                                    </h3>
                                                    <p className="text-muted small mb-0">
                                                        {String(question.type || '').replace(/_/g, ' ')}
                                                        {' · '}
                                                        {formatCount(question.totalAnswers)} answer
                                                        {question.totalAnswers === 1 ? '' : 's'}
                                                    </p>
                                                </div>
                                            </div>
                                            {usePie ? (
                                                <DashboardPieChart
                                                    series={series}
                                                    solidColors
                                                    showDataLabels
                                                />
                                            ) : (
                                                <DashboardBarChart
                                                    series={series}
                                                    solidColors
                                                    horizontal={!isRating}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="ecx-page-card survey-results__chart-empty">
                                <i className="bi bi-bar-chart-line fs-3 text-muted d-block mb-2" />
                                <h3 className="h6 mb-1">No graph data yet</h3>
                                <p className="text-muted small mb-0">
                                    Charts appear here after patients submit choice or rating answers.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default SurveyGraphsPage;
